import IndicatorVsQuoteChart from "components/charts/modern/IndicatorVsQuoteChart";
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import { CompanyQuotesTabEnum } from "components/company/v3/menu/enums";
import FiisHistoricalReturnContent from "components/company/v3/realestatefunds/contents/quote/FiisHistoricalReturnContent";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import { Provider } from "jotai";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { DollarSignIcon, LineChartIcon, PercentIcon, TrendingUpIcon } from "lucide-react";
import { useIntl } from "react-intl";
import { ISecurityCountry } from "types/graphqlTypes";

interface FiisQuotesBoxProps {
  tickerSymbol: string;
  tab: CompanyQuotesTabEnum;
  onChangeTab: (tab: CompanyQuotesTabEnum) => void;
}

export default function FiisQuotesBox({ tickerSymbol, tab, onChangeTab }: FiisQuotesBoxProps) {
  const intl = useIntl();
  const selfie = useSelfie();

  return (
    <CompanyContainer
      className={classNames({
        "xl:h-full xl:min-h-company-box": [CompanyQuotesTabEnum.QUOTE].includes(tab),
        "xl:h-fit": [CompanyQuotesTabEnum.HISTORICAL_RETURN].includes(tab),
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyQuotesTabEnum.QUOTE}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyQuotesTabEnum)}
          className="h-auto w-full"
        >
          <CompanyTabsList
            activeTab={tab}
            tabs={[
              {
                value: CompanyQuotesTabEnum.QUOTE,
                label: intl.formatMessage({
                  defaultMessage: "Cotação",
                  description: "Aba do menu superior da empresa",
                  id: "uWNGsP",
                }),
                icon: <LineChartIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              selfie?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.HISTORICAL_RETURN,
                    label: intl.formatMessage({
                      defaultMessage: "Retorno Histórico",
                      description: "Aba do menu superior da empresa",
                      id: "oM9BVy",
                    }),
                    icon: <PercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              selfie?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.PRICE_VS_INDICATOR,
                    label: intl.formatMessage({
                      defaultMessage: "Cotação x Rendimento/Cota",
                      description: "Aba do menu superior da empresa",
                      id: "I/DxWr",
                    }),
                    icon: <TrendingUpIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              selfie?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.MARKET_CAPITALIZATION,
                    label: intl.formatMessage({
                      defaultMessage: "Valor de Mercado",
                      description: "Aba do menu superior da empresa",
                      id: "VITY4x",
                    }),
                    icon: <DollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
            ].filter(notUndefinedOrNull)}
          />

          <CompanyTabsContent value={CompanyQuotesTabEnum.QUOTE}>
            <Provider>
              <QuotesChart tickerSymbols={[tickerSymbol]} />
            </Provider>
          </CompanyTabsContent>

          {selfie?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.HISTORICAL_RETURN}>
              <FiisHistoricalReturnContent />
            </CompanyTabsContent>
          )}

          {selfie?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.PRICE_VS_INDICATOR}>
              <Provider>
                <IndicatorVsQuoteChart country={ISecurityCountry.Br} tickerSymbols={[tickerSymbol]} />
              </Provider>
            </CompanyTabsContent>
          )}

          {selfie?.isPremium && (
            <CompanyTabsContent value={CompanyQuotesTabEnum.MARKET_CAPITALIZATION}>
              <Provider>
                <MarketCapitalizationChart tickerSymbols={[tickerSymbol]} />
              </Provider>
            </CompanyTabsContent>
          )}
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
